import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sun, Moon, Coffee, Apple, Droplet, Check, X, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const MEAL_TYPES = [
  { key: "breakfast", label: "Breakfast", icon: Coffee },
  { key: "lunch", label: "Lunch", icon: Sun },
  { key: "dinner", label: "Dinner", icon: Moon },
  { key: "snacks", label: "Snacks", icon: Apple },
];

function logFor(logs, mt) { return logs.find((l) => l.meal_type === mt); }

export default function PatientToday() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // {meal_type, log}
  const [actualFood, setActualFood] = useState("");
  const [notes, setNotes] = useState("");
  const [time, setTime] = useState("");
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = () => {
    api.get("/patient/today")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const planDay = (() => {
    if (!data?.plan) return null;
    if (data.plan.plan_type === "general") return null;
    // Pick day_number 1 for MVP (or based on start_date)
    return data.plan.days?.[0] || null;
  })();

  const quickMark = async (mt, status) => {
    try {
      await api.post("/meal-logs", { date: data.date, meal_type: mt, status });
      toast.success(status === "completed" ? "Marked complete" : "Marked skipped");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPlanPdf = async () => {
    if (!data?.plan?.plan_id) return;
    setDownloading(true);
    try {
      const res = await api.get(`/diet-plans/${data.plan.plan_id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (data.plan.name || "diet-plan").replace(/[^a-z0-9\-_ ]/gi, "").replace(/\s+/g, "_") || "diet-plan";
      a.download = `${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Diet plan downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDownloading(false);
    }
  };

  const openDetails = (mt) => {
    const existing = logFor(data.logs, mt);
    setActive({ meal_type: mt, log: existing });
    setActualFood(existing?.actual_food || "");
    setNotes(existing?.notes || "");
    setTime(existing?.time || "");
  };

  const saveDetails = async () => {
    setSaving(true);
    try {
      await api.post("/meal-logs", {
        date: data.date, meal_type: active.meal_type,
        status: active.log?.status === "skipped" ? "skipped" : "completed",
        actual_food: actualFood, notes, time
      });
      toast.success("Logged");
      setActive(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const addWater = async (ml) => {
    try {
      await api.post("/water-logs", { date: data.date, ml });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="text-muted-foreground">Loading…</div>;
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="patient-today-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Today</p>
          <h1 className="text-3xl font-bold mt-1">Hi {user?.name?.split(" ")[0] || "there"} 👋</h1>
          <p className="text-muted-foreground mt-1">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</p>
        </div>
        {data.plan && (
          <Button
            type="button"
            variant="outline"
            onClick={downloadPlanPdf}
            disabled={downloading}
            className="gap-2 rounded-full"
            data-testid="download-plan-pdf"
          >
            {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {downloading ? "Preparing…" : "Download diet (PDF)"}
          </Button>
        )}
      </header>

      {/* Plan */}
      {!data.plan ? (
        <div className="nt-card p-6 text-center text-muted-foreground" data-testid="no-plan-card">
          <p>No active diet plan yet.</p>
          <p className="text-sm mt-1">Your dietitian will assign one soon.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {data.plan.plan_type === "general" && (
            <div className="nt-card p-5">
              <h2 className="font-semibold mb-3">Your structure</h2>
              <div className="space-y-3">
                {MEAL_TYPES.map((m) => (
                  <div key={m.key} data-testid={`general-meal-${m.key}`}>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">{m.label}</div>
                    <div className="text-sm mt-1">{data.plan.general_structure?.[m.key] || "—"}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {MEAL_TYPES.map((m) => {
            const Icon = m.icon;
            const meal = planDay?.meals?.[m.key];
            const existing = logFor(data.logs, m.key);
            const completed = existing?.status === "completed";
            const skipped = existing?.status === "skipped";
            return (
              <div key={m.key} className="nt-card p-5" data-testid={`meal-card-${m.key}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${completed ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}>
                      <Icon size={18} />
                    </div>
                    <div>
                      <div className="font-semibold capitalize">{m.label}</div>
                      {meal?.items?.length ? (
                        <ul className="mt-1 space-y-0.5">
                          {meal.items.map((it, i) => (
                            <li key={i} className="text-sm text-muted-foreground">
                              • {it.name}{it.qty ? ` — ${it.qty}${it.unit || ""}` : ""}
                            </li>
                          ))}
                        </ul>
                      ) : data.plan.plan_type === "general" ? (
                        <div className="text-sm text-muted-foreground mt-1">{data.plan.general_structure?.[m.key] || "—"}</div>
                      ) : (
                        <div className="text-sm text-muted-foreground mt-1">No items planned</div>
                      )}
                      {meal?.notes && <div className="text-xs text-muted-foreground mt-2 italic">{meal.notes}</div>}
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex gap-2 flex-wrap">
                  <Button
                    size="sm"
                    onClick={() => quickMark(m.key, "completed")}
                    variant={completed ? "default" : "outline"}
                    className="gap-1 rounded-full"
                    data-testid={`mark-complete-${m.key}`}
                  >
                    <Check size={14} /> {completed ? "Completed" : "Mark complete"}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => quickMark(m.key, "skipped")}
                    variant={skipped ? "secondary" : "outline"}
                    className="gap-1 rounded-full"
                    data-testid={`mark-skip-${m.key}`}
                  >
                    <X size={14} /> {skipped ? "Skipped" : "Skip"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => openDetails(m.key)} className="rounded-full" data-testid={`open-details-${m.key}`}>
                    Add what I ate
                  </Button>
                </div>
                {existing?.actual_food && (
                  <div className="mt-3 text-sm bg-muted rounded-lg p-3">
                    <span className="font-medium">You ate: </span>{existing.actual_food}
                    {existing.notes && <div className="text-muted-foreground mt-1">{existing.notes}</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Water tracker */}
      <div className="nt-card p-5" data-testid="water-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Droplet size={18} className="text-primary" />
              <h2 className="font-semibold">Water intake</h2>
            </div>
            <p className="text-2xl font-bold mt-2" data-testid="water-total">{data.water_ml} ml</p>
            <p className="text-xs text-muted-foreground">Goal: 2000 ml</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => addWater(250)} className="rounded-full" data-testid="water-add-250">+250 ml</Button>
            <Button size="sm" variant="outline" onClick={() => addWater(500)} className="rounded-full" data-testid="water-add-500">+500 ml</Button>
          </div>
        </div>
        <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-primary transition-all" style={{ width: `${Math.min(100, (data.water_ml / 2000) * 100)}%` }} />
        </div>
      </div>

      {/* Meal details modal */}
      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent data-testid="meal-detail-dialog">
          <DialogHeader><DialogTitle className="capitalize">{active?.meal_type} — what did you eat?</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>What you ate</Label>
              <Textarea rows={3} value={actualFood} onChange={(e) => setActualFood(e.target.value)} placeholder="e.g. 2 boiled eggs, toast, banana" data-testid="actual-food-input" />
            </div>
            <div className="space-y-2">
              <Label>Time</Label>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="meal-time-input" />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="meal-notes-input" />
            </div>
            <Button onClick={saveDetails} disabled={saving} className="w-full" data-testid="save-meal-details-button">
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
